/* @jsx h */
import {
  defineComponent,
  h,
  PropType,
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
} from "vue";
import { LemmonModal } from "../Modal";

export type ListMultiSelectItem = {
  id: number;
  name: string;
};

export const ListMultiSelect = defineComponent({
  name: "ListMultiSelect",
  props: {
    items: {
      type: Array as PropType<ListMultiSelectItem[]>,
      required: true,
    },
    selectedIds: {
      type: Array as PropType<number[]>,
      required: true,
    },
    onChange: {
      type: Function as PropType<(selectedIds: number[]) => void>,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    buttonLabel: {
      type: String,
      default: "Items",
    },
    searchPlaceholder: {
      type: String,
      default: "Search...",
    },
    // Compact mode shows only the label plus a small count badge (never the
    // selected names), keeping the trigger narrow.
    compact: {
      type: Boolean,
      default: false,
    },
    // Override the trigger width. Defaults to the roomy layout used elsewhere.
    widthClass: {
      type: String,
      default: "w-full sm:w-[360px]",
    },
  },
  setup(props) {
    const open = ref(false);
    const search = ref("");
    const rootRef = ref<HTMLElement | null>(null);
    const pendingIds = ref<number[]>([...props.selectedIds]);
    const isMobile = ref(false);

    const sortedItems = computed(() =>
      [...props.items].sort((a, b) => a.name.localeCompare(b.name)),
    );

    const filteredItems = computed(() => {
      const query = search.value.trim().toLowerCase();
      if (!query) {
        return sortedItems.value;
      }
      return sortedItems.value.filter((item) =>
        item.name.toLowerCase().includes(query),
      );
    });

    const selectedNames = computed(() => {
      const selected = new Set(props.selectedIds);
      return sortedItems.value
        .filter((item) => selected.has(item.id))
        .map((item) => item.name);
    });

    const selectionSummary = computed(() => {
      if (props.selectedIds.length === 0) {
        return "All items";
      }
      if (selectedNames.value.length <= 2) {
        return selectedNames.value.join(", ");
      }
      return `${props.selectedIds.length} selected`;
    });

    const toggleItem = (itemId: number) => {
      const next = pendingIds.value.includes(itemId)
        ? pendingIds.value.filter((id) => id !== itemId)
        : [...pendingIds.value, itemId];
      pendingIds.value = next;
    };

    const clearSelection = () => {
      pendingIds.value = [];
    };

    const selectVisible = () => {
      const visibleIds = filteredItems.value.map((item) => item.id);
      pendingIds.value = [...new Set([...pendingIds.value, ...visibleIds])];
    };

    const applySelection = () => {
      props.onChange(pendingIds.value);
      open.value = false;
    };

    const closeWithoutApply = () => {
      pendingIds.value = [...props.selectedIds];
      open.value = false;
    };

    const handleDocumentClick = (event: MouseEvent) => {
      if (isMobile.value || !rootRef.value) {
        return;
      }
      if (!rootRef.value.contains(event.target as Node)) {
        closeWithoutApply();
      }
    };

    const updateViewport = () => {
      isMobile.value = window.innerWidth < 640;
    };

    onMounted(() => {
      updateViewport();
      document.addEventListener("mousedown", handleDocumentClick);
      window.addEventListener("resize", updateViewport);
    });

    onBeforeUnmount(() => {
      document.removeEventListener("mousedown", handleDocumentClick);
      window.removeEventListener("resize", updateViewport);
    });

    const renderSelectionControls = (showTitle: boolean) => (
      <>
        <div class="flex items-center justify-between gap-2 border-b border-base-300 px-4 py-3">
          {showTitle ? (
            <div class="text-sm font-semibold">{props.buttonLabel}</div>
          ) : (
            <div />
          )}
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="btn btn-ghost btn-xs"
              onClick={selectVisible}
              disabled={filteredItems.value.length === 0}
            >
              Select visible
            </button>
            <button
              type="button"
              class="btn btn-ghost btn-xs"
              onClick={clearSelection}
            >
              Clear
            </button>
          </div>
        </div>

        <div class="p-4">
          <label class="input input-bordered flex items-center gap-2">
            <span class="material-symbols-outlined text-base-content/60">
              search
            </span>
            <input
              type="text"
              class="grow min-w-0"
              placeholder={props.searchPlaceholder}
              value={search.value}
              onInput={(event) => {
                search.value = (event.target as HTMLInputElement).value;
              }}
            />
          </label>
        </div>

        <div class="max-h-72 overflow-y-auto px-2 pb-2">
          {props.loading ? (
            <div class="px-3 py-6 text-center text-sm text-base-content/70">
              Loading...
            </div>
          ) : filteredItems.value.length === 0 ? (
            <div class="px-3 py-6 text-center text-sm text-base-content/70">
              No items found
            </div>
          ) : (
            filteredItems.value.map((item) => (
              <label
                key={item.id}
                class="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2 hover:bg-base-200"
              >
                <input
                  type="checkbox"
                  class="checkbox checkbox-sm checkbox-primary"
                  checked={pendingIds.value.includes(item.id)}
                  onChange={() => toggleItem(item.id)}
                />
                <span class="truncate">{item.name}</span>
              </label>
            ))
          )}
        </div>
      </>
    );

    const renderApplyButton = () => (
      <div class="border-t border-base-300 bg-base-100 p-4">
        <button
          type="button"
          class="btn btn-primary w-full"
          onClick={applySelection}
        >
          Apply
        </button>
      </div>
    );

    return () => (
      <div ref={rootRef} class={`relative ${props.widthClass}`}>
        <button
          type="button"
          class="btn btn-outline w-full justify-between gap-2 normal-case"
          onClick={() => {
            if (!open.value) {
              pendingIds.value = [...props.selectedIds];
            }
            open.value = !open.value;
          }}
        >
          <span class="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
            <span class="material-symbols-outlined flex-shrink-0 text-lg">
              list
            </span>
            {props.compact ? (
              <span class="truncate text-ellipsis">{props.buttonLabel}</span>
            ) : (
              <span class="truncate text-ellipsis">
                {props.buttonLabel}: {selectionSummary.value}
              </span>
            )}
          </span>
          <span class="flex flex-shrink-0 items-center gap-1">
            {props.compact && props.selectedIds.length > 0 ? (
              <span class="badge badge-primary badge-sm">
                {props.selectedIds.length}
              </span>
            ) : null}
            <span class="material-symbols-outlined text-lg">
              {open.value ? "expand_less" : "expand_more"}
            </span>
          </span>
        </button>

        {open.value && isMobile.value ? (
          <LemmonModal
            title={props.buttonLabel}
            onClose={closeWithoutApply}
            renderButtons={renderApplyButton}
          >
            {renderSelectionControls(false)}
          </LemmonModal>
        ) : open.value ? (
          <div
            class={`absolute top-[calc(100%+0.5rem)] z-20 rounded-2xl border border-base-300 bg-base-100 shadow-2xl ${
              props.compact
                ? "left-0 min-w-[260px] sm:min-w-[300px]"
                : "left-0 right-0"
            }`}
          >
            {renderSelectionControls(true)}
            {renderApplyButton()}
          </div>
        ) : null}
      </div>
    );
  },
});
